/**
 * Centralized section parsing utilities for consistent heading formats across all components
 * 
 * STANDARD FORMAT:
 * - H1 (#) = Document title (not a section)
 * - H2 (##) = Main sections (used for regeneration, editing, preview)
 * - H3+ (###, ####, etc.) = Subsections within main sections
 */

export interface ParsedSection {
  id: string;
  title: string;
  content: string;
  level: number;
  order: number;
}

export interface SectionMetadata {
  type: 'static' | 'dynamic' | 'ai-assisted';
  permissions?: 'read-only' | 'editable' | 'admin-only';
  aiPrompt?: string; // For ai-assisted sections
  dataSource?: string; // For dynamic sections
  refreshInterval?: number; // For dynamic sections
}

export interface TemplateSection {
  id: string;
  title: string;
  content: string;
  edited: boolean;
  metadata?: SectionMetadata;
}

/**
 * Parse markdown content into sections using consistent H2 (##) standard
 * This is the SINGLE SOURCE OF TRUTH for section parsing
 */
export const parseMarkdownSections = (content: string): ParsedSection[] => {
  if (!content) return [];

  const lines = content.split("\n");
  const sections: ParsedSection[] = [];
  let currentSection: ParsedSection | null = null;
  let sectionOrder = 0;
  let contentLines: string[] = [];

  console.log('🔧 Parsing sections with H2 standard from content:', content.substring(0, 100));
  console.log('🔧 Total content length:', content.length);
  console.log('🔧 Total lines:', lines.length);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check for H2 headers (##) - our standard for main sections
    const h2Match = line.match(/^##\s+(.+)$/);
    if (h2Match) {
      // Save previous section if exists
      if (currentSection) {
        const sectionContent = contentLines.join('\n').trim();
        currentSection.content = sectionContent;
        sections.push(currentSection);
        console.log(`💾 Saved section "${currentSection.title}" with ${sectionContent.length} chars`);
      }

      // Start new section
      const title = h2Match[1].trim();
      currentSection = {
        id: `section-${sectionOrder + 1}`,
        title,
        content: '',
        level: 2,
        order: sectionOrder++
      };
      contentLines = [];

      console.log(`📋 Found H2 section: "${title}" (order: ${sectionOrder - 1})`);
    } else if (currentSection) {
      // Add content to current section (including H3+ subsections)
      contentLines.push(line);
    } else {
      // Content before first H2 section - log for debugging
      if (line.trim()) {
        console.log(`⚠️ Content before first H2 section: "${line.substring(0, 50)}..."`);
      }
    }
    // Skip H1 titles and content before first H2 section
  }

  // Add final section
  if (currentSection) {
    const finalSectionContent = contentLines.join('\n').trim();
    currentSection.content = finalSectionContent;
    sections.push(currentSection);
    console.log(`💾 Saved final section "${currentSection.title}" with ${finalSectionContent.length} chars`);
  }

  console.log(`✅ Parsed ${sections.length} sections using H2 standard:`, sections.map(s => s.title));

  // 🚨 DETAILED DEBUG: Show all parsed sections with content lengths
  console.log("🔍 PARSEMARKDOWNSECTIONS DETAILED DEBUG:");
  sections.forEach((section, index) => {
    console.log(`📋 Section ${index + 1}: "${section.title}"`);
    console.log(`   - ID: ${section.id}`);
    console.log(`   - Content length: ${section.content?.length || 0}`);
    console.log(`   - Content preview: ${section.content?.substring(0, 100) || 'NO CONTENT'}...`);
    console.log(`   - Order: ${section.order}`);
  });

  return sections;
};

/**
 * Parse markdown for template sections (YooptaTemplateEditor format) with metadata support
 */
export const parseMarkdownForTemplateEditor = (content: string): TemplateSection[] => {
  if (!content) return [];

  const lines = content.split('\n');
  const sections: TemplateSection[] = [];
  let currentSection: TemplateSection | null = null;
  let currentMetadata: SectionMetadata | null = null;
  let sectionOrder = 0;

  console.log('🔧 Parsing template sections with metadata support');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for metadata comment
    const metadataMatch = line.match(/<!--\s*METADATA:(.+?)\s*-->/);
    if (metadataMatch) {
      try {
        currentMetadata = JSON.parse(metadataMatch[1]);
        console.log('📋 Found metadata:', currentMetadata);
      } catch (e) {
        console.warn('Failed to parse metadata:', metadataMatch[1]);
        currentMetadata = { type: 'static' };
      }
      continue;
    }

    // Check for H2 header (section start)
    const headerMatch = line.match(/^##\s+(.+)$/);
    if (headerMatch) {
      // Save previous section if exists
      if (currentSection) {
        currentSection.content = currentSection.content.trim();
        sections.push(currentSection);
      }

      // Start new section with metadata
      sectionOrder++;
      currentSection = {
        id: `section-${sectionOrder}`,
        title: headerMatch[1].trim(),
        content: '',
        edited: false,
        metadata: currentMetadata || { type: 'static' }
      };
      currentMetadata = null; // Reset for next section
      console.log('📄 Started new section:', currentSection.title, 'with metadata:', currentSection.metadata);
    } else if (currentSection) {
      // Add content to current section
      currentSection.content += line + '\n';
    }
  }

  // Add final section if exists
  if (currentSection) {
    currentSection.content = currentSection.content.trim();
    sections.push(currentSection);
  }

  console.log(`📊 Parsed ${sections.length} template sections with metadata`);
  return sections;
};

/**
 * Extract section titles only (for template creation)
 */
export const extractSectionTitles = (content: string): string[] => {
  const sections = parseMarkdownSections(content);
  const titles = sections.map(s => s.title);
  
  console.log("📋 Extracted section titles:", titles);
  return titles.length > 0 ? titles : ["Template Content"];
};

/**
 * Parse sections for regeneration (matches server-side logic)
 */
export const parseMarkdownForRegeneration = (content: string) => {
  const lines = content.split("\n");
  const sections = [];
  let documentTitle = '';

  // First, extract document title (H1) but don't treat it as a section
  const h1Match = lines.find(line => line.match(/^#\s+(.+)$/));
  if (h1Match) {
    documentTitle = h1Match.replace(/^#\s+/, '').trim();
    console.log(`📄 Document title: "${documentTitle}"`);
  }

  // Extract H2 headers as sections (consistent with our standard)
  let currentSection = null;
  let sectionContent = "";

  for (const line of lines) {
    // Skip H1 (document title)
    if (line.match(/^#\s+(.+)$/)) {
      continue;
    }

    // Check for H2 headers as sections (our standard)
    const headerMatch = line.match(/^##\s+(.+)$/);
    if (headerMatch) {
      // Save previous section if exists
      if (currentSection) {
        sections.push({
          id: `section-${sections.length + 1}`,
          title: currentSection,
          content: sectionContent.trim(),
        });
      }

      // Start new section
      const title = headerMatch[1].trim();
      currentSection = title;
      sectionContent = "";
    } else if (currentSection) {
      sectionContent += line + "\n";
    }
  }

  // Add final section if exists
  if (currentSection) {
    sections.push({
      id: `section-${sections.length + 1}`,
      title: currentSection,
      content: sectionContent.trim(),
    });
  }

  console.log(`📊 Parsed ${sections.length} sections for regeneration:`, sections.map(s => s.title));
  return sections.length > 0
    ? sections
    : [
        {
          id: "section-1",
          title: "Document Content",
          content: content,
        },
      ];
};

/**
 * Parse sections for document overview (template selection)
 */
export const parseMarkdownForOverview = (content: string) => {
  const sections = parseMarkdownSections(content);
  
  return sections.map((section, index) => ({
    name: section.title,
    color: ["blue", "green", "yellow", "purple", "indigo", "pink"][index % 6],
    description: `Section from template content`,
  }));
};

/**
 * Convert sections back to markdown with consistent H2 format and metadata comments
 */
export const sectionsToMarkdown = (sections: TemplateSection[]): string => {
  return sections
    .map(section => {
      const content = section.content.trim();
      let markdown = '';

      // Add metadata comment before section header if metadata exists
      if (section.metadata && section.metadata.type !== 'static') {
        const metadataJson = JSON.stringify(section.metadata);
        markdown += `<!-- METADATA:${metadataJson} -->\n`;
        console.log('💾 Adding metadata to section:', section.title, section.metadata);
      }

      // Create H2 header with content
      markdown += `## ${section.title}\n\n${content}`;

      return markdown;
    })
    .join('\n\n');
};

/**
 * Validate if content follows our H2 standard
 */
export const validateSectionFormat = (content: string): { isValid: boolean; issues: string[] } => {
  const issues: string[] = [];
  const lines = content.split('\n');
  
  let hasH2Sections = false;
  let hasInconsistentHeaders = false;
  
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      
      if (level === 2) {
        hasH2Sections = true;
      } else if (level === 1) {
        // H1 is OK for document title
      } else if (level > 2) {
        // H3+ is OK for subsections
      } else {
        hasInconsistentHeaders = true;
      }
    }
  }
  
  if (!hasH2Sections) {
    issues.push("No H2 (##) sections found. Use ## for main sections.");
  }
  
  if (hasInconsistentHeaders) {
    issues.push("Inconsistent header levels found. Use ## for main sections.");
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Convert content to our standard H2 format
 */
export const normalizeToH2Standard = (content: string): string => {
  const lines = content.split('\n');
  const normalizedLines: string[] = [];
  
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const title = headerMatch[2].trim();
      
      if (level === 1) {
        // Keep H1 as document title
        normalizedLines.push(line);
      } else if (level <= 3) {
        // Convert H2 and H3 to H2 (main sections)
        normalizedLines.push(`## ${title}`);
      } else {
        // Convert H4+ to H3 (subsections)
        normalizedLines.push(`### ${title}`);
      }
    } else {
      normalizedLines.push(line);
    }
  }
  
  return normalizedLines.join('\n');
};

export default {
  parseMarkdownSections,
  parseMarkdownForTemplateEditor,
  extractSectionTitles,
  parseMarkdownForRegeneration,
  parseMarkdownForOverview,
  sectionsToMarkdown,
  validateSectionFormat,
  normalizeToH2Standard
};
